import type { FetchResult } from '@apollo/client';

import { client, gql } from './Client';

interface IEmailNotificationFIForBulkAssignmentResponse {
  data: {
    emailNotificationFIForBulkAssignment: string;
  };
}

interface IEmailNotificationLIForBulkSignStamp {
  data: {
    emailNotificationLIForBulkSignStamp: string;
  };
}

interface IEmailNotificationFIForBulkResponse {
  data: {
    emailNotificationFIForBulk: string;
  };
}

interface IAdhocEmailNotificationFIForBulkResponse {
  data: {
    adhocEmailNotificationFIForBulk: string;
  };
}

interface IAdhocEmailNotificationLIForBulkSignStampResponse {
  data: {
    adhocEmailNotificationLIForBulkSignStamp: string;
  };
}

interface IBulkTaskandInspectionsList {
  inspectionId: number;
  taskId: number;
  previousFI: number;
  leadInspectorId: number;
  isActionByFI?: boolean;
}

interface IEmailNotificationSCAfterBulkSignStamp {
  data: {
    emailNotificationSCAfterBulkSignStamp: string;
  };
}

const emailNotificationForReAssignFIMutation = gql`
  mutation emailNotificationFIForBulk(
    $scheduleId: Int!
    $inspectorField: Int!
    $inspectorLead: Int!
    $clerkId: Int!
    $isReassignedByFI: Boolean!
    $bulkTaskandInspectionsList: [BulkTaskandInspectionInput]
  ) {
    emailNotificationFIForBulk(
      scheduleId: $scheduleId
      inspectorField: $inspectorField
      inspectorLead: $inspectorLead
      clerkId: $clerkId
      bulkTaskandInspectionsList: $bulkTaskandInspectionsList
      isReassignedByFI: $isReassignedByFI
    )
  }
`;

const emailForTaskAssignmentMutation = gql`
  mutation emailNotificationFIForBulkAssignment(
    $scheduleId: Int!
    $inspectorField: Int!
    $inspectorLead: Int!
    $clerkId: Int!
    $bulkTaskandInspectionsList: [BulkTaskandInspectionInput]
    $isAssignedByFI: Boolean!
  ) {
    emailNotificationFIForBulkAssignment(
      scheduleId: $scheduleId
      inspectorField: $inspectorField
      inspectorLead: $inspectorLead
      clerkId: $clerkId
      bulkTaskandInspectionsList: $bulkTaskandInspectionsList
      isAssignedByFI: $isAssignedByFI
    )
  }
`;

const emailForTaskBulkSignAndStampMutation = gql`
  mutation emailNotificationLIForBulkSignStamp(
    $scheduleId: Int!
    $inspectorField: Int!
    $inspectorLead: Int!
    $bulkTaskandInspectionsList: [BulkTaskandInspectionInput]
    $isAssignedByFI: Boolean!
  ) {
    emailNotificationLIForBulkSignStamp(
      scheduleId: $scheduleId
      inspectorField: $inspectorField
      inspectorLead: $inspectorLead
      bulkTaskandInspectionsList: $bulkTaskandInspectionsList
      isAssignedByFI: $isAssignedByFI
    )
  }
`;

const adhocEmailNotificationFIForBulk = gql`
  mutation adhocEmailNotificationFIForBulk(
    $siteId: Int!
    $inspectorField: Int!
    $bulkTaskandInspectionsList: [BulkTaskandInspectionInput]
  ) {
    adhocEmailNotificationFIForBulk(
      siteId: $siteId
      inspectorField: $inspectorField
      bulkTaskandInspectionsList: $bulkTaskandInspectionsList
    )
  }
`;

const adhocEmailNotificationLIForBulkSignStamp = gql`
  mutation adhocEmailNotificationLIForBulkSignStamp(
    $siteId: Int!
    $bulkTaskandInspectionsList: [BulkTaskandInspectionInput]
  ) {
    adhocEmailNotificationLIForBulkSignStamp(
      siteId: $siteId
      bulkTaskandInspectionsList: $bulkTaskandInspectionsList
    )
  }
`;

const emailNotificationSCAfterBulkSignStamp = gql`
  mutation emailNotificationSCAfterBulkSignStamp(
    $scheduleId: Int!
    $inspectorLead: Int!
    $bulkTaskandInspectionsList: [BulkTaskandInspectionInput]
  ) {
    emailNotificationSCAfterBulkSignStamp(
      scheduleId: $scheduleId
      inspectorLead: $inspectorLead
      bulkTaskandInspectionsList: $bulkTaskandInspectionsList
    )
  }
`;

const emailNotificationReAssignByFI = (
  scheduleId: number,
  inspectorField: number,
  inspectorLead: number,
  clerkId: number,
  bulkTaskandInspectionsList: { inspectionId: number; taskId: number }[],
  isReassignedByFI: boolean,
): Promise<FetchResult<IEmailNotificationFIForBulkResponse>> =>
  client.mutate({
    mutation: emailNotificationForReAssignFIMutation,
    variables: {
      scheduleId,
      inspectorField,
      inspectorLead,
      clerkId,
      isReassignedByFI,
      bulkTaskandInspectionsList,
    },
  });

const emailForTaskAssignment = (
  scheduleId: number,
  inspectorField: number,
  inspectorLead: number,
  clerkId: number,
  bulkTaskandInspectionsList: { inspectionId: number }[],
  isAssignedByFI: boolean,
): Promise<FetchResult<IEmailNotificationFIForBulkAssignmentResponse>> =>
  client.mutate({
    mutation: emailForTaskAssignmentMutation,
    variables: {
      scheduleId,
      inspectorField,
      inspectorLead,
      clerkId,
      bulkTaskandInspectionsList,
      isAssignedByFI,
    },
  });

const emailForTaskBulkSignAndStamp = (
  scheduleId: number,
  inspectorField: number,
  inspectorLead: number,
  bulkTaskandInspectionsList: { inspectionId: number }[],
  isAssignedByFI: boolean,
): Promise<FetchResult<IEmailNotificationLIForBulkSignStamp>> =>
  client.mutate({
    mutation: emailForTaskBulkSignAndStampMutation,
    variables: {
      scheduleId,
      inspectorField,
      inspectorLead,
      bulkTaskandInspectionsList,
      isAssignedByFI,
    },
  });

const emailForAdhocTaskBulkReassign = (
  siteId: number,
  inspectorField: number,
  clerkId: number,
  bulkTaskandInspectionsList: IBulkTaskandInspectionsList[],
): Promise<FetchResult<IAdhocEmailNotificationFIForBulkResponse>> =>
  client.mutate({
    mutation: adhocEmailNotificationFIForBulk,
    variables: {
      siteId,
      inspectorField,
      clerkId,
      bulkTaskandInspectionsList,
    },
  });

const emailForAdhocTaskBulkSignAndStamp = (
  siteId: number,
  bulkTaskandInspectionsList: IBulkTaskandInspectionsList[],
): Promise<FetchResult<IAdhocEmailNotificationLIForBulkSignStampResponse>> =>
  client.mutate({
    mutation: adhocEmailNotificationLIForBulkSignStamp,
    variables: {
      siteId,
      bulkTaskandInspectionsList,
    },
  });

const emailForScAfterLiSign = (
  scheduleId: number,
  inspectorLead: number,
  bulkTaskandInspectionsList: { inspectionId: number }[],
): Promise<FetchResult<IEmailNotificationSCAfterBulkSignStamp>> =>
  client.mutate({
    mutation: emailNotificationSCAfterBulkSignStamp,
    variables: {
      scheduleId,
      inspectorLead,
      bulkTaskandInspectionsList,
    },
  });

export {
  emailNotificationReAssignByFI,
  emailForTaskAssignment,
  emailForTaskBulkSignAndStamp,
  emailForAdhocTaskBulkReassign,
  emailForAdhocTaskBulkSignAndStamp,
  emailForScAfterLiSign,
};
